import { t } from "@lingui/macro"
import {
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    styled,
    Divider,
    Typography,
    LinearProgress,
    Box,
} from "@mui/material"
import { observer } from "mobx-react"
import { useCallback, useEffect } from "react"

import { ConfirmModal } from "src/modals/confirm"
import {
    CopyPropertySettingsStore,
    ICopyPropertySettingsPayload,
} from "src/modals/copy-property-settings/store"
import { GlobalStore } from "src/store"
import { StoreProvider } from "src/store/lib/StoreProvider"
import { useStore } from "src/store/lib/useStore"
import { IProperty } from "src/views/client-management/property/store"

const Wrap = styled(Stack)({ maxHeight: "inherit" })

const List = styled("ul")({
    overflow: "auto",
    margin: 0,
    padding: "0 0 0 1rem",
})

const ListItem = styled("li")({
    listStyle: "none",
})

interface IProps {
    properties: IProperty[]
    settings: ICopyPropertySettingsPayload
    sourcePropertyName: string
    description?: string
}

const View = observer((props: IProps) => {
    const store = useStore(CopyPropertySettingsStore)
    const gstore = useStore(GlobalStore)

    useEffect(() => {
        store.init(props.properties, props.settings)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store])

    const handleConfirmCopyClick = useCallback(() => {
        gstore.modals.open(
            () => (
                <ConfirmModal
                    onConfirm={async (confirmed) => {
                        if (confirmed) {
                            await store.submit()
                            gstore.modals.pop() // Close confirm modal
                            gstore.modals.pop() // Close copy settings modal
                        }
                    }}
                    title={t`copy-property-settings-modal.confirm-title`}
                    content={t`copy-property-settings-modal.confirm-content`}
                />
            ),
            { variant: "slide-up-w600" },
        )
    }, [gstore, store])

    const copyButtonIsDisabled =
        !store.hasSelected() ||
        gstore.loading.is(CopyPropertySettingsStore.LoadingKeys.submit)

    return (
        <Wrap>
            <DialogTitle>
                {t`copy-property-settings-modal.modal-header`}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ mb: 2 }}>
                    {props.description != null
                        ? props.description
                        : t({
                              id: "copy-property-settings-modal.description",
                              values: { name: props.sourcePropertyName },
                          })}
                </Typography>
                {store.isSubmitting && store.totalToUpdate > 5 && (
                    <Box sx={{ mb: 2 }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 1,
                            }}
                        >
                            <Typography variant="body2">
                                {t`copy-property-settings-modal.progress-label`}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                                {store.progress} / {store.totalToUpdate} (
                                {store.progressPercentage}%)
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={store.progressPercentage}
                        />
                    </Box>
                )}
                <List>
                    {store.properties.length > 0 && (
                        <>
                            <ListItem>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={store.allSelected}
                                            indeterminate={
                                                store.hasSelected() &&
                                                !store.allSelected
                                            }
                                            onChange={(_, checked) =>
                                                store.setAllSelected(checked)
                                            }
                                        />
                                    }
                                    label={
                                        <Typography fontWeight={600}>
                                            {t`copy-property-settings-modal.select-all`}{" "}
                                            ({store.properties.length})
                                        </Typography>
                                    }
                                />
                            </ListItem>
                            <Divider
                                component="li"
                                sx={{ my: 1, listStyle: "none" }}
                            />
                        </>
                    )}
                    {store.properties.map((property) => (
                        <ListItem key={property.propertyId}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={store.willCopy(property)}
                                        onChange={() =>
                                            store.toggleCopy(property)
                                        }
                                    />
                                }
                                label={property.name}
                            />
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "flex-end", padding: "16px" }}>
                <Button color="info" onClick={() => gstore.modals.pop()}>
                    {t`global.cancel`}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirmCopyClick}
                    disabled={copyButtonIsDisabled}
                >
                    {t`copy-property-settings-modal.copy-button`}
                </Button>
            </DialogActions>
        </Wrap>
    )
})

export const CopyPropertySettingsModal = observer((props: IProps) => (
    <StoreProvider Store={CopyPropertySettingsStore}>
        <View {...props} />
    </StoreProvider>
))
